import { telemetryInternalIds } from './telemetry-identifiers';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { requestIdFor, requestStatusFor } from './request-correlation';
import {
  FA_ACTION_NAMES,
  filterStructuredLog,
  structuredEventLevel,
  StructuredEvent,
  LogTestContext,
} from './structured-log-policy';

export function logCorrelationId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const value = Math.floor(Math.random() * 16);
      return (character === 'x' ? value : (value & 3) | 8).toString(16);
    })
  );
}

export function logFailureReason(error: unknown): {
  reason_code: string;
  http_status?: number;
} {
  const status = requestStatusFor(error);
  if (typeof status !== 'number') return { reason_code: 'unknown' };
  const reason_code =
    status === 401 || status === 403
      ? 'permission_denied'
      : status === 409
        ? 'stale_state'
        : status === 400 || status === 422
          ? 'business_rule'
          : status === 0
            ? 'network'
            : status === 408 || status === 504
              ? 'timeout'
              : status >= 500
                ? 'dependency_unavailable'
                : 'unknown';
  return { reason_code, http_status: status };
}

@Injectable({ providedIn: 'root' })
export class StructuredLogger {
  emit(event: StructuredEvent, attributes: Record<string, unknown>): void {
    try {
      const level = structuredEventLevel(event);
      const log = filterStructuredLog({ message: event, level, attributes });
      if (log) Sentry.logger[level](event, log.attributes);
    } catch {
      // Telemetry must never interrupt the operation being observed.
    }
  }

  action(name: FaActionName, testContext?: LogTestContext): FaActionSequence {
    return new FaActionSequence(this, name, testContext);
  }
}

export type FaActionName = (typeof FA_ACTION_NAMES)[number];

/** Scoped to one dialog or resource/action pair; admits reviewed identifier fields only. */
export class FaActionSequence {
  private readonly interactionId = logCorrelationId();
  private attempts = 0;
  private previousFailure: string | undefined;

  constructor(
    private readonly logger: StructuredLogger,
    private readonly name: FaActionName,
    private readonly testContext?: LogTestContext,
  ) {}

  begin(identifiers: Record<string, unknown> = {}) {
    const started = Date.now();
    const attemptId = logCorrelationId();
    const attributes = {
      ...telemetryInternalIds(identifiers),
      ...this.testContext,
      action_name: this.name,
      feature: this.name.split('.')[0],
      actor_type: 'fa',
      trigger: 'user',
      interaction_id: this.interactionId,
      attempt_id: attemptId,
      attempt_number: ++this.attempts,
    };
    let finished = false;
    const finish = (
      event: StructuredEvent | null,
      details: Record<string, unknown>,
    ) => {
      if (finished) return;
      finished = true;
      if (event)
        this.logger.emit(event, {
          ...attributes,
          ...details,
          duration_ms: Math.max(0, Date.now() - started),
        });
    };
    return {
      succeed: (
        stage: string,
        accepted = false,
        identifiers: Record<string, unknown> = {},
      ) => {
        if (finished) return;
        finish(this.previousFailure ? 'fa.action.recovered' : null, {
          ...telemetryInternalIds(identifiers),
          stage,
          outcome: accepted ? 'accepted' : 'succeeded',
          recovery_of_attempt_id: this.previousFailure,
        });
        this.previousFailure = undefined;
      },
      fail: (
        error: unknown,
        stage: string,
        dispatched: boolean,
        blockedReason?: string,
        progress: Record<string, unknown> = {},
        uncertain = false,
      ) => {
        if (finished) return;
        const reason = logFailureReason(error);
        const status = reason.http_status;
        const blocked =
          !uncertain &&
          (!!blockedReason ||
            (status !== undefined &&
              [400, 401, 403, 404, 409, 422].includes(status)));
        const unknown = !blocked && dispatched;
        finish(
          blocked
            ? 'fa.action.blocked'
            : unknown
              ? 'fa.action.outcome_unknown'
              : 'fa.action.failed',
          {
            ...progress,
            ...reason,
            request_id: requestIdFor(error),
            ...(blockedReason ? { reason_code: blockedReason } : {}),
            stage,
            outcome: blocked ? 'blocked' : unknown ? 'unknown' : 'failed',
          },
        );
        this.previousFailure = attemptId;
      },
    };
  }
}
